import { type CSSProperties, type ReactElement } from 'react';

import {
  NOTHING_DAMAGED,
  NOTHING_FOR_SALE,
  NOTHING_TO_SELL,
  READING_NOTE,
  STASH_NOTE,
  WORN_NOTE,
  type ExchangeKind,
  type OfferRow,
  type RepairRow,
  type SaleRow,
  type VendorPanelProps,
  type VendorPending,
  describeVendorFailure,
  exchangeLabelOf,
  exchangeName,
  isExchanging,
  isPendingRow,
  vendorStatusOf,
} from './vendor-model';

export {
  type OfferRow,
  type RepairRow,
  type SaleRow,
  type VendorNote,
  type VendorPanelProps,
  type VendorPending,
  type VendorView,
} from './vendor-model';

/**
 * The town vendor, as a player meets it: what is for sale, what the vendor bids
 * for what you are carrying, and what mending your gear costs.
 *
 * ## It draws numbers. It does not produce them, and neither does anything it
 * is drawn from.
 *
 * Every figure on this panel arrives on `view`, arranged by `./vendor-view.ts`
 * out of two API answers: the ask from the shop window, the bid and the mend
 * from the vendor's quote. There is no multiplier in this file, no comparison
 * against a purse, and no balance: the panel is handed no purse at all, so the
 * arithmetic this project has already banned once — *a balance computed as held
 * minus price* — is not merely avoided here, it is unexpressible. Town draws the
 * purse, from the roster row the server's own answer replaced.
 *
 * ## One press is one exchange, and the control is genuinely `disabled`
 *
 * The same call `town-screen.tsx` makes for Descend, and here the stake is
 * plainer: a second press before the first answer lands is a **second
 * purchase**, and the API being atomic protects the database rather than the
 * player's gold. So while the caller says an exchange is in flight, every
 * control on the panel is a real `disabled` button, which dispatches no click at
 * all — not one styled to look inert.
 *
 * **Every** control, not only the one that was pressed: buying and selling at
 * once would be two decisions made against one purse, and the second would be
 * taken against a balance this client has not been told about yet.
 *
 * Where `skill-tree.tsx` uses `aria-disabled` so a locked cell can answer a click
 * with the reason it is locked, there is nothing here a second press could be
 * told that the pressed control's own label and the live region do not already
 * say.
 *
 * ## Nothing is greyed out for being unaffordable
 *
 * Deliberately, and it is the one place this panel departs from
 * `skill-tree.tsx`'s *"do not ask for what will be refused"*. Whether a purse can
 * cover a price is `gold.ts`'s rule; a client that answered it would be a second
 * copy of the rule that actually charges. Buy stays live at any balance, the
 * server refuses, and the player reads *not enough gold* in the live region —
 * which is more than a greyed button was ever going to tell them.
 *
 * ## Placeholder art
 *
 * The palette is `town-screen.tsx`'s, copied rather than imported, for the
 * reason that file gives: a shared style module written twice by two agents at
 * once is a merge conflict rather than a saving.
 */

const PARCHMENT = '#d8d2c4';
const INK = '#0b0b0d';
const PANEL = '#15151a';
const EDGE = '#3a3730';
const GOLD = '#c8a44c';
const DIM = '#6f6a5e';

const SECTION: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '0.9rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: PANEL,
  color: PARCHMENT,
};

const TITLE: CSSProperties = { margin: 0, fontSize: '0.85rem', letterSpacing: '0.25em', textTransform: 'uppercase' };
const GROUP: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.35rem' };
const GROUP_TITLE: CSSProperties = { margin: 0, color: DIM, fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase' };
const QUIET: CSSProperties = { margin: 0, color: DIM, fontSize: '0.75rem' };

const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.5rem',
  padding: '0.3rem 0.4rem',
  border: `1px solid ${EDGE}`,
  borderRadius: 3,
  background: INK,
  fontSize: '0.8rem',
};

const PRICE: CSSProperties = { marginLeft: 'auto', color: GOLD, fontVariantNumeric: 'tabular-nums' };
const WEAR: CSSProperties = { color: DIM, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' };
const STATUS: CSSProperties = { minHeight: '1.25rem', margin: 0, color: GOLD, fontSize: '0.85rem' };

const buttonStyle = (busy: boolean): CSSProperties => ({
  padding: '0.25rem 0.7rem',
  border: `1px solid ${busy ? EDGE : GOLD}`,
  borderRadius: 3,
  background: busy ? PANEL : EDGE,
  color: busy ? DIM : PARCHMENT,
  font: 'inherit',
  fontSize: '0.75rem',
  cursor: busy ? 'progress' : 'pointer',
});

/**
 * One row's control.
 *
 * The accessible name carries the verb, the thing and the figure, because the
 * visible label is one word repeated down the whole list and the figure is what
 * the press is a decision about.
 */
interface ExchangeButtonProps {
  readonly kind: ExchangeKind;
  readonly id: string;
  readonly name: string;
  readonly gold: number;
  readonly pending: VendorPending | null | undefined;
  readonly onPress: (id: string) => void;
}

const ExchangeButton = ({ kind, id, name, gold, pending, onPress }: ExchangeButtonProps): ReactElement => {
  const mine = isPendingRow(pending, kind, id);

  return (
    <button
      type="button"
      // A real `disabled`, and set by *any* exchange being in flight rather than
      // only by this one. See the header.
      disabled={isExchanging(pending)}
      aria-label={exchangeName(kind, name, gold)}
      style={buttonStyle(mine)}
      onClick={(): void => {
        onPress(id);
      }}
    >
      {exchangeLabelOf(kind, mine)}
    </button>
  );
};

const offerRow = (row: OfferRow, pending: VendorPending | null | undefined, onBuy: (id: string) => void): ReactElement => (
  <div key={row.offerId} data-testid={`vendor-offer-${row.offerId}`} style={ROW}>
    <span>{row.name}</span>
    <span style={PRICE}>{row.price} gold</span>
    <ExchangeButton kind="buy" id={row.offerId} name={row.name} gold={row.price} pending={pending} onPress={onBuy} />
  </div>
);

const saleRow = (row: SaleRow, pending: VendorPending | null | undefined, onSell: (id: string) => void): ReactElement => (
  <div key={row.itemId} data-testid={`vendor-sale-${row.itemId}`} style={ROW}>
    <span>{row.name}</span>
    <span style={PRICE}>{row.bid} gold</span>
    <ExchangeButton kind="sell" id={row.itemId} name={row.name} gold={row.bid} pending={pending} onPress={onSell} />
  </div>
);

const repairRow = (
  row: RepairRow,
  pending: VendorPending | null | undefined,
  onRepair: (id: string) => void,
): ReactElement => (
  <div key={row.itemId} data-testid={`vendor-repair-${row.itemId}`} style={ROW}>
    <span>{row.name}</span>
    {/* Data off the item, not a rule about it: what is left, and what it had. */}
    <span style={WEAR}>
      {row.durability.current}/{row.durability.maximum}
      {row.worn ? ' · worn' : ''}
    </span>
    <span style={PRICE}>{row.cost} gold</span>
    <ExchangeButton kind="repair" id={row.itemId} name={row.name} gold={row.cost} pending={pending} onPress={onRepair} />
  </div>
);

export const VendorPanel = ({
  view,
  failure = null,
  onBuy,
  onSell,
  onRepair,
  pending = null,
  note = null,
}: VendorPanelProps): ReactElement => (
  <section data-testid="vendor-panel" aria-label="Vendor" style={SECTION}>
    <h3 style={TITLE}>Vendor</h3>

    {view === null ? (
      /*
        A counter nobody has read yet is not an empty counter, and drawing the
        first as the second says "the vendor has nothing for you" about a
        request that is still in flight. When the read *failed*, the reason is
        said instead — a panel that reads for ever is a panel with a bug in it
        as far as a player can tell.
      */
      <p data-testid="vendor-reading" style={QUIET}>
        {failure === null ? READING_NOTE : describeVendorFailure(failure)}
      </p>
    ) : (
      <>
        <div data-testid="vendor-stock" role="group" aria-label="For sale" style={GROUP}>
          <h4 style={GROUP_TITLE}>For sale</h4>
          {view.stock.length === 0 ? (
            <p style={QUIET}>{NOTHING_FOR_SALE}</p>
          ) : (
            view.stock.map((row) => offerRow(row, pending, onBuy))
          )}
        </div>

        <div data-testid="vendor-sell" role="group" aria-label="Your pack" style={GROUP}>
          <h4 style={GROUP_TITLE}>Your pack</h4>
          {view.sellable.length === 0 ? (
            <p style={QUIET}>{NOTHING_TO_SELL}</p>
          ) : (
            view.sellable.map((row) => saleRow(row, pending, onSell))
          )}
          {/* Said, not left to be discovered: the vendor *would* buy out of the
              stash, and this panel does not offer it. See `vendor-view.ts`. */}
          <p style={QUIET}>{STASH_NOTE}</p>
        </div>

        <div data-testid="vendor-repair" role="group" aria-label="Damaged" style={GROUP}>
          <h4 style={GROUP_TITLE}>Damaged</h4>
          {view.damaged.length === 0 ? (
            <p style={QUIET}>{NOTHING_DAMAGED}</p>
          ) : (
            view.damaged.map((row) => repairRow(row, pending, onRepair))
          )}
          <p style={QUIET}>{WORN_NOTE}</p>
        </div>
      </>
    )}

    {/*
      The panel's own live region, and deliberately not town's: *"bought a
      dagger"* and *"that run is over"* are two conversations, and one region
      carrying both would let a purchase quietly overwrite the news of a death.
    */}
    <p data-testid="vendor-status" role="status" aria-live="polite" style={STATUS}>
      {vendorStatusOf(pending, note)}
    </p>
  </section>
);
